import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  CircleDollarSign,
  Copy,
  CreditCard,
  Download,
  Hexagon,
  Receipt,
} from 'lucide-react';
import { getApiErrorMessage } from '@/api/axios';
import {
  downloadInvoicePdf,
  getInvoicePayments,
} from '@/api/invoices.api';
import {
  createStripeCheckoutSession,
  getPaymentRails,
  getSupportedPaymentOptions,
} from '@/api/payment.api';
import {
  InvoiceStatus,
  InvoiceStreamEventType,
  InvoiceType,
  PaymentStatus,
  type DiscoveredRail,
  type Invoice,
  type InvoicePaymentHistoryItem,
  type InvoicePaymentHistoryResponse,
  type PaymentCreatePayload,
  type SupportedPaymentOptions,
} from '@/api/types';
import {
  getNetworkIcon,
  getNetworkLabel,
  getTokenIcon,
  getTokenLabel,
} from '@/common/consts';
import { PaymentConfirmationStatus } from '@/components/PaymentConfirmationStatus';
import { useCreatePayment, useInvoice } from '@/hooks';

type SelectOption = {
  value: string;
  label: string;
  icon?: string;
};

function formatIntegerPart(integerPart: string) {
  return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatCryptoAmount(value: string | number) {
  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return '';
  }

  const sign = normalizedValue.startsWith('-') ? '-' : '';
  const unsignedValue = sign ? normalizedValue.slice(1) : normalizedValue;
  const [rawIntegerPart = '0', rawFractionPart = ''] = unsignedValue.split('.');
  const integerPart = rawIntegerPart.replace(/^0+(?=\d)/, '') || '0';
  const trimmedFractionPart = rawFractionPart.replace(/0+$/, '');
  const formattedIntegerPart = formatIntegerPart(integerPart);

  if (!rawFractionPart || !trimmedFractionPart) {
    return `${sign}${formattedIntegerPart}.00`;
  }

  return `${sign}${formattedIntegerPart}.${trimmedFractionPart}`;
}

function getCurrencyFractionDigits(currency: string) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits;
  } catch {
    return null;
  }
}

function formatAmountByCurrency(value: string | number, currency?: string) {
  if (!currency) {
    return formatCryptoAmount(value);
  }

  const fractionDigits = getCurrencyFractionDigits(currency);

  if (fractionDigits === null) {
    return formatCryptoAmount(value);
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return formatCryptoAmount(value);
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numericValue);
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatTransactionHash(txId: string) {
  const normalizedTxId = txId.trim();
  const prefixLength = normalizedTxId.startsWith('0x') ? 8 : 6;
  const suffixLength = 4;

  if (normalizedTxId.length <= prefixLength + suffixLength + 3) {
    return normalizedTxId;
  }

  return `${normalizedTxId.slice(0, prefixLength)}...${normalizedTxId.slice(-suffixLength)}`;
}

function buildDisplayName(
  firstName?: string | null,
  lastName?: string | null,
  fallback = 'Not provided',
) {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return fullName || fallback;
}

function getLabelInitials(value?: string | null, fallback = 'PL') {
  const initials = (value ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return initials || fallback;
}

function triggerFileDownload(blob: Blob, fileName: string) {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 0);
}

function getStatusLabel(status: InvoiceStatus) {
  switch (status) {
    case InvoiceStatus.PAID:
      return 'PAID';
    case InvoiceStatus.CANCELLED:
      return 'CANCELLED';
    default:
      return 'UNPAID';
  }
}

function getStatusBadgeClassName(status: InvoiceStatus) {
  switch (status) {
    case InvoiceStatus.PAID:
      return 'bg-emerald-500/15 text-emerald-300';
    case InvoiceStatus.CANCELLED:
      return 'bg-amber-500/15 text-amber-300';
    default:
      return 'bg-[#e97724] text-white';
  }
}

function getInvoiceTypeLabel(type: InvoiceType) {
  return type === InvoiceType.CRYPTO ? 'CRYPTO' : 'FIAT';
}

function getPaymentStatusLabel(status?: PaymentStatus | null) {
  switch (status) {
    case PaymentStatus.PENDING:
      return 'Pending';
    case PaymentStatus.PARTIAL:
      return 'Partially paid';
    case PaymentStatus.COMPLETED:
      return 'Completed';
    case PaymentStatus.EXPIRED:
      return 'Expired';
    case PaymentStatus.CANCELLED:
      return 'Cancelled';
    default:
      return 'No active payment';
  }
}

function getPaymentStatusClassName(status?: PaymentStatus | null) {
  switch (status) {
    case PaymentStatus.PARTIAL:
      return 'border-sky-700 bg-sky-900/30 text-sky-200';
    case PaymentStatus.EXPIRED:
      return 'border-amber-700 bg-amber-900/30 text-amber-200';
    case PaymentStatus.COMPLETED:
      return 'border-emerald-700 bg-emerald-900/30 text-emerald-200';
    case PaymentStatus.CANCELLED:
      return 'border-amber-700 bg-amber-900/30 text-amber-200';
    default:
      return 'border-[#2a2b32] bg-[#111215] text-gray-300';
  }
}

function shouldReloadPaymentHistory(type: InvoiceStreamEventType) {
  return (
    type === InvoiceStreamEventType.PAYMENT_PARTIAL ||
    type === InvoiceStreamEventType.PAID
  );
}

function resolveAcceptedPaymentAssets(invoice: Invoice | null): string[] | null {
  if (!invoice) {
    return null;
  }

  if (invoice.acceptedPaymentAssets?.length) {
    return invoice.acceptedPaymentAssets;
  }

  if (invoice.invoiceType === InvoiceType.CRYPTO && invoice.currency) {
    return [invoice.currency];
  }

  return null;
}

function formatAcceptedPaymentAssets(assets: string[] | null) {
  if (!assets || assets.length === 0) {
    return 'All currently supported assets';
  }

  return assets.map((asset) => getTokenLabel(asset)).join(', ');
}

type PaymentMethodKind = 'CRYPTO' | 'STRIPE';

/**
 * Payer-facing wording for a visible-but-unavailable card rail. Hidden rails
 * never reach this: the discovery endpoint marks a method the merchant never
 * set up as hidden and the UI must not render it at all.
 */
function getCardUnavailableText(rail: DiscoveredRail): string {
  switch (rail.unavailableReason) {
    case 'TENANT_NOT_ONBOARDED':
      return 'The merchant has not finished setting up card payments.';
    case 'CHARGES_DISABLED':
      return 'Card payments are temporarily unavailable for this merchant.';
    case 'CURRENCY_NOT_SUPPORTED':
      return 'Card payments are not available for this invoice currency.';
    case 'INVOICE_TYPE_NOT_SUPPORTED':
      return 'Card payments are not available for this type of invoice.';
    case 'INVOICE_NOT_PAYABLE':
      return 'There is nothing left to pay on this invoice.';
    case 'TEMPORARILY_UNAVAILABLE':
      return 'Card payments are temporarily unavailable. Please try again shortly.';
    default:
      return rail.detail ?? 'Card payments are currently unavailable.';
  }
}

function PaymentSelect({
  label,
  value,
  options,
  onChange,
  icon,
  placeholder,
  disabled = false,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
  icon: ReactNode;
  placeholder: string;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === value);
  const isInteractive = !disabled && !!onChange && options.length > 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <label className="mb-2 block text-xs text-gray-400">{label}</label>

      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => {
          if (!isInteractive) {
            return;
          }

          setIsOpen((open) => !open);
        }}
        className={`flex w-full items-center justify-between rounded-xl border border-[#2a2b32] bg-[#111215] p-3 text-left transition-colors ${
          isInteractive
            ? 'cursor-pointer hover:border-gray-600'
            : 'cursor-default opacity-80'
        }`}
      >
        <span className="flex items-center gap-3">
          {selectedOption?.icon ? (
            <img
              src={selectedOption.icon}
              alt={selectedOption.label}
              className="h-6 w-6 rounded-full bg-white/95 p-0.5"
            />
          ) : (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-white">
              {icon}
            </span>
          )}
          <span className="text-sm font-medium text-white">
            {selectedOption?.label ?? placeholder}
          </span>
        </span>

        <ChevronDown
          className={`h-5 w-5 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isInteractive && isOpen && (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-20 overflow-hidden rounded-xl border border-[#2a2b32] bg-[#111215] shadow-2xl">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`flex w-full items-center gap-3 px-3 py-3 text-left text-sm transition-colors ${
                option.value === value
                  ? 'bg-white/5 text-[#7dd3fc]'
                  : 'text-white hover:bg-white/5'
              }`}
            >
              {option.icon ? (
                <img
                  src={option.icon}
                  alt={option.label}
                  className="h-6 w-6 rounded-full bg-white/95 p-0.5"
                />
              ) : (
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-white">
                  {icon}
                </span>
              )}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InvoiceDetails({
  invoice,
  downloadLoading,
  downloadError,
  onDownloadPdf,
}: {
  invoice: Invoice;
  downloadLoading: boolean;
  downloadError: string | null;
  onDownloadPdf: () => Promise<void>;
}) {
  /*
   * No fallback to `invoiceCreator*`. Those fields are populated from the
   * invoice's customer — the party being billed — so falling back to them
   * printed the payer's own name as the merchant they owe. Removing the
   * "Created by" line above took away the label; this took away the lie.
   *
   * It barely fires anyway: the API resolves `merchantLegalName` from the
   * tenant's legal name or its name, and the latter is never null — so this is
   * an invoice with no customer, which is exactly when there is no name to
   * borrow.
   */
  const merchantName = invoice.merchantLegalName?.trim() || 'Merchant';
  const customerName = buildDisplayName(
    invoice.customerFirstName,
    invoice.customerLastName,
  );
  const billingLocation = [invoice.billingCity, invoice.billingPostalCode]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(', ');
  const billingAddress = [
    invoice.billingAddressLine?.trim(),
    billingLocation,
    invoice.billingCountry?.trim(),
  ].filter(Boolean);

  return (
    <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold">Invoice Details</h2>
          <span
            className={`rounded-full px-3 py-1 text-xs font-bold tracking-wide ${getStatusBadgeClassName(invoice.status)}`}
          >
            {getStatusLabel(invoice.status)}
          </span>
        </div>

        <button
          type="button"
          onClick={() => {
            void onDownloadPdf();
          }}
          disabled={downloadLoading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#2a2b32] bg-[#111215] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:border-gray-500 hover:bg-[#1b1d22] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Download className="h-4 w-4" />
          {downloadLoading ? 'Preparing PDF...' : 'Download PDF'}
        </button>
      </div>

      {downloadError && (
        <div className="mb-6 rounded-xl border border-red-700 bg-red-900/30 px-4 py-3 text-sm text-red-200">
          {downloadError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-3">
        <div>
          <p className="mb-2 text-xs text-gray-400">Merchant</p>
          <div className="flex items-center gap-3">
            {invoice.merchantLogoUrl ? (
              <img
                src={invoice.merchantLogoUrl}
                alt={`${merchantName} logo`}
                className="h-10 w-10 rounded-xl border border-[#2a2b32] bg-white object-cover p-1"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#ff9a4d] via-[#f97316] to-[#8b5cf6] text-sm font-semibold text-white">
                {getLabelInitials(merchantName)}
              </div>
            )}
            <div>
              <span className="text-sm font-medium">{merchantName}</span>
            </div>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Customer</p>
          <p className="text-sm font-medium">{customerName}</p>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Reference ID</p>
          <p className="text-sm font-medium">{invoice.referenceId}</p>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Invoice Type</p>
          <div className="flex items-center gap-2">
            <Hexagon className="h-5 w-5 text-blue-400" />
            <span className="text-sm font-medium">
              {getInvoiceTypeLabel(invoice.invoiceType)}
            </span>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Tax ID</p>
          <p className="text-sm font-medium">
            {invoice.taxId?.trim() || 'Not provided'}
          </p>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Company Registration</p>
          <p className="text-sm font-medium">
            {invoice.companyRegistrationNumber?.trim() || 'Not provided'}
          </p>
        </div>

        <div className="md:col-span-2">
          <p className="mb-2 text-xs text-gray-400">Billing Address</p>
          <p className="text-sm font-medium">
            {billingAddress.length > 0
              ? billingAddress.join(', ')
              : 'Not provided'}
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="mb-2 text-xs text-gray-400">Description</p>
          <p className="text-sm font-medium">
            {invoice.description?.trim() || 'No description provided.'}
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="mb-2 text-xs text-gray-400">Latest Payment Status</p>
          <div
            className={`rounded-xl border px-4 py-3 text-sm ${getPaymentStatusClassName(invoice.paymentStatus)}`}
          >
            <p className="font-medium">
              {getPaymentStatusLabel(invoice.paymentStatus)}
            </p>
            {invoice.paymentExpiredAt && (
              <p className="mt-1 text-xs opacity-80">
                Active until {formatDateTime(invoice.paymentExpiredAt)}
              </p>
            )}
          </div>
        </div>

        {invoice.paymentConfirmation && (
          <div className="md:col-span-3">
            <PaymentConfirmationStatus
              confirmation={invoice.paymentConfirmation}
              fallbackNetwork={invoice.paymentConfirmation.network}
              fallbackAsset={invoice.paymentConfirmation.asset}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function PaymentSummary({
  invoice,
  formattedTotalAmount,
  formattedPaidAmount,
  formattedRemainingToPay,
}: {
  invoice: Invoice;
  formattedTotalAmount: string;
  formattedPaidAmount: string;
  formattedRemainingToPay: string;
}) {
  return (
    <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 shadow-sm">
      <h2 className="mb-6 text-lg font-semibold">Payment Summary</h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div>
          <p className="mb-2 text-xs text-gray-400">Total Amount</p>
          <p className="text-2xl font-bold">
            {formattedTotalAmount} {invoice.currency}
          </p>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Paid Amount</p>
          <p className="text-2xl font-bold">
            {formattedPaidAmount} {invoice.currency}
          </p>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Remaining to Pay</p>
          <p className="text-2xl font-bold">
            {formattedRemainingToPay} {invoice.currency}
          </p>
        </div>
      </div>
    </section>
  );
}

function TransactionHistory({
  transactions,
  loading,
  error,
  page,
  total,
  totalPages,
  onPrevPage,
  onNextPage,
}: {
  transactions: InvoicePaymentHistoryItem[];
  loading: boolean;
  error: string | null;
  page: number;
  total: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
}) {
  const [copiedTxId, setCopiedTxId] = useState<string | null>(null);
  const [failedTxId, setFailedTxId] = useState<string | null>(null);

  useEffect(() => {
    if (!copiedTxId && !failedTxId) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setCopiedTxId(null);
      setFailedTxId(null);
    }, 2000);

    return () => window.clearTimeout(timeout);
  }, [copiedTxId, failedTxId]);

  const handleCopyTxId = async (txId: string) => {
    if (!navigator.clipboard?.writeText) {
      setCopiedTxId(null);
      setFailedTxId(txId);
      return;
    }

    try {
      await navigator.clipboard.writeText(txId);
      setCopiedTxId(txId);
      setFailedTxId(null);
    } catch {
      setCopiedTxId(null);
      setFailedTxId(txId);
    }
  };

  return (
    <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Transaction History</h2>
        <p className="text-sm text-gray-400">
          {total > 0 ? `${total} recorded payment${total === 1 ? '' : 's'}` : ''}
        </p>
      </div>

      <div className="mb-4 hidden grid-cols-4 gap-4 border-b border-[#2a2b32] pb-3 text-xs text-gray-400 md:grid">
        <div>Date</div>
        <div>Asset</div>
        <div>Amount</div>
        <div>Tx ID</div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-[#2a2b32] bg-[#111215] px-4 py-6 text-sm text-gray-300">
          Loading payment history...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-700 bg-red-900/30 px-4 py-6 text-sm text-red-200">
          {error}
        </div>
      ) : transactions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <Receipt className="mb-3 h-8 w-8 opacity-50" />
          <p className="text-sm">
            There are no incoming transactions for this invoice.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div
                key={`${transaction.txId}-${transaction.date}`}
                className="grid grid-cols-1 gap-3 rounded-xl border border-[#2a2b32] bg-[#111215] px-4 py-3 text-sm text-white md:grid-cols-4 md:gap-4"
              >
                <div>
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Date</p>
                  <div>{formatDateTime(transaction.date)}</div>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Asset</p>
                  <div>{getTokenLabel(transaction.asset)}</div>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Amount</p>
                  <div>
                    {formatCryptoAmount(transaction.amount)} {transaction.asset}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Tx ID</p>
                  <div className="flex items-center gap-2">
                    <code
                      className="truncate text-gray-300"
                      title={transaction.txId}
                    >
                      {formatTransactionHash(transaction.txId)}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        void handleCopyTxId(transaction.txId);
                      }}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[#3a3b45] bg-[#22242a] px-2 py-1 text-xs font-medium text-gray-100 transition-colors hover:bg-[#2a2d34]"
                      aria-label="Copy transaction hash"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                  {copiedTxId === transaction.txId && (
                    <p className="mt-1 text-xs text-emerald-300">
                      Transaction hash copied.
                    </p>
                  )}
                  {failedTxId === transaction.txId && (
                    <p className="mt-1 text-xs text-red-300">
                      Could not copy transaction hash.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#2a2b32] pt-4 text-sm">
              <p className="text-gray-400">
                Page {page} of {totalPages}
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onPrevPage}
                  disabled={page <= 1}
                  className="rounded-lg border border-[#2a2b32] px-3 py-2 text-white transition-colors hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={onNextPage}
                  disabled={page >= totalPages}
                  className="rounded-lg border border-[#2a2b32] px-3 py-2 text-white transition-colors hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function PaymentMethod({
  invoice,
  invoiceLoading,
  invoiceError,
  optionsLoading,
  optionsError,
  selectionError,
  isCryptoInvoice,
  isInvoiceClosed,
  selectedNetwork,
  selectedToken,
  acceptedAssetsLabel,
  networkOptions,
  assetOptions,
  formattedBalanceDue,
  loading,
  error,
  setNetwork,
  setToken,
  onSubmit,
  method,
  setMethod,
  cardRail,
  cardLoading,
  cardError,
  onPayWithCard,
}: {
  invoice: Invoice | null;
  invoiceLoading: boolean;
  invoiceError: string | null;
  optionsLoading: boolean;
  optionsError: string | null;
  selectionError: string | null;
  isCryptoInvoice: boolean;
  isInvoiceClosed: boolean;
  selectedNetwork: string;
  selectedToken: string;
  acceptedAssetsLabel: string;
  networkOptions: SelectOption[];
  assetOptions: SelectOption[];
  formattedBalanceDue: string;
  loading: boolean;
  error: string | null;
  setNetwork: (value: string) => void;
  setToken: (value: string) => void;
  onSubmit: () => Promise<void>;
  method: PaymentMethodKind;
  setMethod: (value: PaymentMethodKind) => void;
  cardRail: DiscoveredRail | null;
  cardLoading: boolean;
  cardError: string | null;
  onPayWithCard: () => Promise<void>;
}) {
  const isCardMethod = method === 'STRIPE' && cardRail !== null;

  return (
    <aside className="sticky top-6 rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 shadow-sm">
      <h2 className="mb-6 text-lg font-semibold">Choose a Payment Method</h2>

      {cardRail && !isInvoiceClosed && (
        <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl border border-[#2a2b32] bg-[#111215] p-1">
          <button
            type="button"
            onClick={() => setMethod('CRYPTO')}
            className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-colors ${
              method === 'CRYPTO'
                ? 'bg-[#009bf2] text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <Hexagon className="h-4 w-4" />
            Crypto
          </button>
          <button
            type="button"
            onClick={() => setMethod('STRIPE')}
            className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-colors ${
              method === 'STRIPE'
                ? 'bg-[#009bf2] text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <CreditCard className="h-4 w-4" />
            Card
          </button>
        </div>
      )}

      {invoiceLoading && (
        <div className="mb-5 rounded-xl border border-[#2a2b32] bg-[#111215] p-3 text-sm text-gray-300">
          Loading invoice...
        </div>
      )}

      {invoiceError && (
        <div className="mb-5 rounded-xl border border-red-700 bg-red-900/30 p-3 text-sm text-red-200">
          {invoiceError}
        </div>
      )}

      {optionsLoading && (
        <div className="mb-5 rounded-xl border border-[#2a2b32] bg-[#111215] p-3 text-sm text-gray-300">
          Loading supported payment options...
        </div>
      )}

      {optionsError && (
        <div className="mb-5 rounded-xl border border-red-700 bg-red-900/30 p-3 text-sm text-red-200">
          {optionsError}
        </div>
      )}

      {selectionError && !isCardMethod && (
        <div className="mb-5 rounded-xl border border-amber-700 bg-amber-900/30 p-3 text-sm text-amber-200">
          {selectionError}
        </div>
      )}

      {!invoiceLoading && !invoiceError && invoice && !isCardMethod && (
        <div className="mb-5 rounded-xl border border-[#2a2b32] bg-[#111215] p-3 text-sm text-gray-300">
          Accepted assets for this invoice: {acceptedAssetsLabel}
        </div>
      )}

      {!isInvoiceClosed && isCardMethod && cardRail && (
        <div className="flex flex-col gap-5">
          <div className="mb-2 mt-2">
            <p className="mb-1 text-xs text-gray-400">Balance Due</p>
            <p className="text-3xl font-bold">
              {formattedBalanceDue}{' '}
              <span className="text-white">{invoice?.currency ?? ''}</span>
            </p>
            <p className="mt-2 text-xs text-gray-400">
              Cards charge the full remaining balance in one payment.
            </p>
          </div>

          {!cardRail.available && (
            <div className="rounded-xl border border-amber-700 bg-amber-900/30 p-3 text-sm text-amber-200">
              {getCardUnavailableText(cardRail)}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              void onPayWithCard();
            }}
            disabled={
              !cardRail.available ||
              cardLoading ||
              !invoice ||
              invoiceLoading ||
              !!invoiceError
            }
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[#009bf2] py-4 font-medium text-white shadow-[0_0_25px_rgba(0,155,242,0.35)] transition-all hover:bg-[#008ce0] hover:shadow-[0_0_35px_rgba(0,155,242,0.5)] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {cardLoading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg" />
                Redirecting to secure checkout...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5" />
                <span>Pay with Card</span>
              </>
            )}
          </button>

          {cardError && (
            <div className="rounded-xl border border-red-700 bg-red-900/30 p-3 text-sm text-red-200">
              {cardError}
            </div>
          )}

          <div className="rounded-xl border border-[#2a2b32] bg-[#111215] p-4 text-sm text-gray-400">
            You will be redirected to a secure Stripe checkout page. The
            invoice is marked paid once the payment is confirmed.
          </div>
        </div>
      )}

      {!isInvoiceClosed && !isCardMethod && (
        <div className="flex flex-col gap-5">
          <PaymentSelect
            label="Network"
            value={selectedNetwork}
            options={networkOptions}
            onChange={setNetwork}
            icon={<Hexagon className="h-4 w-4" />}
            placeholder="Select network"
            disabled={optionsLoading || networkOptions.length <= 1}
          />

          <PaymentSelect
            label="Asset"
            value={selectedToken}
            options={assetOptions}
            onChange={setToken}
            icon={<CircleDollarSign className="h-4 w-4" />}
            placeholder="Select asset"
            disabled={
              optionsLoading || assetOptions.length <= 1 || isCryptoInvoice
            }
          />

          <div className="mb-2 mt-2">
            <p className="mb-1 text-xs text-gray-400">Balance Due</p>
            <p className="text-3xl font-bold">
              {formattedBalanceDue}{' '}
              <span className="text-white">{invoice?.currency ?? ''}</span>
            </p>
            {!isCryptoInvoice && selectedToken && (
              <p className="mt-2 text-xs text-gray-400">
                The exact {selectedToken} amount will be confirmed on the next
                step.
              </p>
            )}
          </div>

          {invoice?.paymentStatus && (
            <div
              className={`rounded-xl border px-4 py-3 text-sm ${getPaymentStatusClassName(invoice.paymentStatus)}`}
            >
              Active payment: {getPaymentStatusLabel(invoice.paymentStatus)}
              {invoice.paymentExpiredAt && (
                <span className="block pt-1 text-xs opacity-80">
                  Valid until {formatDateTime(invoice.paymentExpiredAt)}
                </span>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              void onSubmit();
            }}
            disabled={
              !selectedNetwork ||
              !selectedToken ||
              loading ||
              !invoice ||
              invoiceLoading ||
              optionsLoading ||
              !!invoiceError ||
              !!optionsError ||
              !!selectionError
            }
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[#009bf2] py-4 font-medium text-white shadow-[0_0_25px_rgba(0,155,242,0.35)] transition-all hover:bg-[#008ce0] hover:shadow-[0_0_35px_rgba(0,155,242,0.5)] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg" />
                Processing...
              </>
            ) : (
              <>
                <span>Proceed to Payment</span>
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>

          {error && (
            <div className="rounded-xl border border-red-700 bg-red-900/30 p-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <div className="rounded-xl border border-[#2a2b32] bg-[#111215] p-4 text-sm text-gray-400">
            {isCryptoInvoice
              ? 'The asset is locked to the invoice accepted payment asset. Choose one of the supported networks to continue.'
              : 'Select a supported network and one of the accepted assets before proceeding. Network fees apply separately.'}
          </div>
        </div>
      )}

      {isInvoiceClosed && (
        <div
          className={`rounded-xl border p-4 text-sm ${
            invoice?.status === InvoiceStatus.PAID
              ? 'border-emerald-700 bg-emerald-900/30 text-emerald-200'
              : 'border-amber-700 bg-amber-900/30 text-amber-200'
          }`}
        >
          {invoice?.status === InvoiceStatus.PAID
            ? 'This invoice has already been paid.'
            : 'This invoice has been cancelled.'}
        </div>
      )}
    </aside>
  );
}

export default function Home() {
  const [network, setNetwork] = useState('');
  const [token, setToken] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsReloadKey, setPaymentsReloadKey] = useState(0);
  const [paymentsData, setPaymentsData] =
    useState<InvoicePaymentHistoryResponse | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [supportedOptions, setSupportedOptions] =
    useState<SupportedPaymentOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [visibleRails, setVisibleRails] = useState<DiscoveredRail[] | null>(
    null,
  );
  const [method, setMethodState] = useState<PaymentMethodKind>('CRYPTO');
  const [methodChosen, setMethodChosen] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const setMethod = (value: PaymentMethodKind) => {
    setMethodState(value);
    setMethodChosen(true);
    setCardError(null);
  };

  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { invoice, loading: invoiceLoading, error: invoiceError } = useInvoice(
    invoiceId,
    {
      enableStream: true,
      onStreamEvent: (type) => {
        if (
          type === InvoiceStreamEventType.SNAPSHOT ||
          !shouldReloadPaymentHistory(type)
        ) {
          return;
        }

        setPaymentsReloadKey((currentKey) => currentKey + 1);
      },
    },
  );
  const { submit, loading, error } = useCreatePayment();

  useEffect(() => {
    let isCancelled = false;

    setOptionsLoading(true);
    setOptionsError(null);

    getSupportedPaymentOptions()
      .then((res) => {
        if (!isCancelled) {
          setSupportedOptions(res.data);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setSupportedOptions(null);
          setOptionsError(
            getApiErrorMessage(error, 'Failed to load supported payment options'),
          );
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setOptionsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    if (!invoiceId) {
      setVisibleRails(null);
      return;
    }

    // Rail discovery is additive: when it fails the page degrades to the
    // crypto-only behaviour it always had, rather than blocking payment.
    getPaymentRails(invoiceId)
      .then((res) => {
        if (isCancelled) {
          return;
        }

        const visible = res.data.rails.filter(
          (rail) => rail.visibility === 'visible',
        );
        setVisibleRails(visible);
      })
      .catch(() => {
        if (!isCancelled) {
          setVisibleRails(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [invoiceId]);

  useEffect(() => {
    setPaymentsPage(1);
  }, [invoiceId]);

  useEffect(() => {
    setDownloadError(null);
  }, [invoiceId]);

  useEffect(() => {
    let isCancelled = false;

    if (!invoiceId) {
      setPaymentsData(null);
      setPaymentsError('Invoice ID missing');
      setPaymentsLoading(false);
      return;
    }

    setPaymentsLoading(true);
    setPaymentsError(null);

    getInvoicePayments(invoiceId, paymentsPage)
      .then((res) => {
        if (!isCancelled) {
          setPaymentsData(res.data);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setPaymentsData(null);
          setPaymentsError(
            getApiErrorMessage(error, 'Failed to load payment history'),
          );
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setPaymentsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [invoiceId, paymentsPage, paymentsReloadKey]);

  const isCryptoInvoice = invoice?.invoiceType === InvoiceType.CRYPTO;
  const isInvoicePaid = invoice?.status === InvoiceStatus.PAID;
  const isInvoiceCancelled = invoice?.status === InvoiceStatus.CANCELLED;
  const isInvoiceClosed = isInvoicePaid || isInvoiceCancelled;

  const cardRail =
    visibleRails?.find((rail) => rail.rail === 'STRIPE') ?? null;
  const cryptoRail =
    visibleRails?.find((rail) => rail.rail === 'CRYPTO') ?? null;

  // Until the payer picks a method themselves, follow the discovery: if
  // crypto cannot currently take a payment and cards can, start on Card.
  useEffect(() => {
    if (methodChosen || !visibleRails) {
      return;
    }

    if (cardRail?.available && cryptoRail && !cryptoRail.available) {
      setMethodState('STRIPE');
    }
  }, [methodChosen, visibleRails, cardRail, cryptoRail]);
  const acceptedPaymentAssets = resolveAcceptedPaymentAssets(invoice);
  const acceptedPaymentAssetSet = new Set(acceptedPaymentAssets ?? []);
  const acceptedAssetsLabel = formatAcceptedPaymentAssets(acceptedPaymentAssets);

  const supportedNetworks = supportedOptions?.networks ?? [];
  const availableNetworks = supportedNetworks.filter((networkOption) => {
    if (!acceptedPaymentAssets) {
      return true;
    }

    return networkOption.assets.some((asset) =>
      acceptedPaymentAssetSet.has(asset.asset),
    );
  });
  const networkOptions = availableNetworks.map((networkOption) => ({
    value: networkOption.network,
    label: getNetworkLabel(networkOption.network),
    icon: getNetworkIcon(networkOption.network),
  }));
  const resolvedNetwork =
    availableNetworks.find((networkOption) => networkOption.network === network) ??
    availableNetworks[0];
  const availableAssets = (resolvedNetwork?.assets ?? []).filter((assetOption) => {
    if (!acceptedPaymentAssets) {
      return true;
    }

    return acceptedPaymentAssetSet.has(assetOption.asset);
  });
  const assetOptions = availableAssets.map((assetOption) => ({
    value: assetOption.asset,
    label: getTokenLabel(assetOption.asset),
    icon: getTokenIcon(assetOption.asset),
  }));

  useEffect(() => {
    if (networkOptions.length === 0) {
      if (network) {
        setNetwork('');
      }
      return;
    }

    if (!networkOptions.some((option) => option.value === network)) {
      setNetwork(networkOptions[0].value);
    }
  }, [network, networkOptions]);

  useEffect(() => {
    if (assetOptions.length === 0) {
      if (token) {
        setToken('');
      }
      return;
    }

    if (!assetOptions.some((option) => option.value === token)) {
      setToken(assetOptions[0].value);
    }
  }, [assetOptions, token]);

  const selectionError =
    !invoice || optionsLoading
      ? null
      : networkOptions.length === 0
        ? 'No supported payment network is available for the accepted invoice assets.'
        : assetOptions.length === 0
          ? 'No accepted assets are available for the selected network.'
          : null;

  const formattedBalanceDue = invoice
    ? formatAmountByCurrency(invoice.remainingAmountWithFee, invoice.currency)
    : '';
  const formattedTotalAmount = invoice
    ? formatAmountByCurrency(invoice.totalAmount, invoice.currency)
    : '';
  const formattedPaidAmount = invoice
    ? formatAmountByCurrency(invoice.paidAmount, invoice.currency)
    : '';

  const handleSubmit = async () => {
    if (
      !network ||
      !token ||
      !invoice ||
      !invoiceId ||
      isInvoiceClosed ||
      selectionError
    ) {
      return;
    }

    const payload: PaymentCreatePayload = {
      network,
      asset: token,
      invoiceId,
    };

    try {
      const payment = await submit(payload);
      navigate(`/invoice/${invoiceId}/payment-details`, {
        state: {
          ...payment,
          invoiceId,
        },
      });
    } catch {
      return;
    }
  };

  const handlePayWithCard = async () => {
    if (!invoiceId || !cardRail?.available || isInvoiceClosed) {
      return;
    }

    setCardLoading(true);
    setCardError(null);

    try {
      const { data } = await createStripeCheckoutSession(invoiceId);
      // Full redirect, not a popup: Stripe hosted Checkout owns the flow and
      // sends the payer back to this page when it finishes.
      window.location.assign(data.checkoutUrl);
    } catch (error) {
      setCardError(getApiErrorMessage(error, 'Failed to start card payment'));
      setCardLoading(false);
    }
  };

  const handleDownloadInvoicePdf = async () => {
    if (!invoiceId) {
      setDownloadError('Invoice ID missing');
      return;
    }

    setDownloadLoading(true);
    setDownloadError(null);

    try {
      const { blob, fileName } = await downloadInvoicePdf(invoiceId);
      triggerFileDownload(blob, fileName);
    } catch (error) {
      setDownloadError(
        getApiErrorMessage(error, 'Failed to download invoice PDF'),
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen justify-center overflow-hidden bg-[#0d0e12] p-4 text-white md:p-8 lg:p-12">
      <div className="pointer-events-none absolute right-[-5%] top-[-10%] h-[600px] w-[600px] rounded-full bg-orange-900/10 blur-[120px]" />
      <div className="pointer-events-none absolute right-[15%] top-[10%] h-2 w-2 rounded-full bg-yellow-100 opacity-50 blur-[2px]" />
      <div className="pointer-events-none absolute right-[25%] top-[5%] h-1.5 w-1.5 rounded-full bg-yellow-100 opacity-40 blur-[1px]" />

      <div className="relative z-10 flex w-full max-w-6xl flex-col gap-6">
        <header className="mb-2">
          <h1 className="mb-1 text-3xl font-bold">Invoice Payment</h1>
          <p className="text-sm text-gray-400">
            Invoice ID: {invoice?.id ?? invoiceId ?? 'Not available'}
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-6 lg:col-span-2">
            {invoice && (
              <>
                <InvoiceDetails
                  invoice={invoice}
                  downloadLoading={downloadLoading}
                  downloadError={downloadError}
                  onDownloadPdf={handleDownloadInvoicePdf}
                />
                <PaymentSummary
                  invoice={invoice}
                  formattedTotalAmount={formattedTotalAmount}
                  formattedPaidAmount={formattedPaidAmount}
                  formattedRemainingToPay={formattedBalanceDue}
                />
                {(method === 'CRYPTO' || !cardRail) && (
                <TransactionHistory
                  transactions={paymentsData?.data ?? []}
                  loading={paymentsLoading}
                  error={paymentsError}
                  page={paymentsData?.page ?? paymentsPage}
                  total={paymentsData?.total ?? 0}
                  totalPages={paymentsData?.totalPages ?? 0}
                  onPrevPage={() => {
                    setPaymentsPage((currentPage) => Math.max(currentPage - 1, 1));
                  }}
                  onNextPage={() => {
                    setPaymentsPage((currentPage) => {
                      const totalPages = paymentsData?.totalPages ?? currentPage;
                      return Math.min(currentPage + 1, totalPages);
                    });
                  }}
                />
                )}
              </>
            )}

            {!invoice && invoiceLoading && (
              <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 text-sm text-gray-300">
                Loading invoice details...
              </section>
            )}

            {!invoice && invoiceError && (
              <section className="rounded-2xl border border-red-700 bg-red-900/30 p-6 text-sm text-red-200">
                {invoiceError}
              </section>
            )}
          </div>

          <div className="lg:col-span-1">
            <PaymentMethod
              invoice={invoice}
              invoiceLoading={invoiceLoading}
              invoiceError={invoiceError}
              optionsLoading={optionsLoading}
              optionsError={optionsError}
              selectionError={selectionError}
              isCryptoInvoice={isCryptoInvoice}
              isInvoiceClosed={isInvoiceClosed}
              selectedNetwork={network}
              selectedToken={token}
              acceptedAssetsLabel={acceptedAssetsLabel}
              networkOptions={networkOptions}
              assetOptions={assetOptions}
              formattedBalanceDue={formattedBalanceDue}
              loading={loading}
              error={error}
              setNetwork={setNetwork}
              setToken={setToken}
              onSubmit={handleSubmit}
              method={method}
              setMethod={setMethod}
              cardRail={cardRail}
              cardLoading={cardLoading}
              cardError={cardError}
              onPayWithCard={handlePayWithCard}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
